import { useRecoilValue } from "recoil";
import fleetWorkOrderAtom from "../../atoms/fleetWorkOrderAtom";
import estimateFleetImageAtom from "../../atoms/estimateFleetImageAtom";
import { btnOne, btnRound } from "../../utils/style";
import arrowBlack from '../../assets/icons/arrow_black.png'
import returnDiscountedAmount from "../../helper_functions/returnDiscountedAmount";
import { calculateTotalDiscount } from "../../helper_functions/estimateCalculation";
import { url } from "../../utils/config";
import fleetAppointmentDetailsAtom from "../../atoms/fleetAppointmentDetailsAtom";


function FleetPreviewEstimate({ setOrderState, updateFleetWorkOrder }) {
    const workOrder = useRecoilValue(fleetWorkOrderAtom)
    const appointmentDetails = useRecoilValue(fleetAppointmentDetailsAtom);
    const technicianCost = appointmentDetails?.technicianHourlyCost;
    const estimateImage = useRecoilValue(estimateFleetImageAtom);
    const defaultTaxCost = appointmentDetails?.taxRatePercent;

    function calculateTotalMinutes() {
        let totalMinutes = 0;

        workOrder?.serviceList?.data?.forEach(data => totalMinutes += +data.estimatedTime);
        workOrder?.materialList?.data?.forEach(data => totalMinutes += +data.estimatedTime);
        workOrder?.otherList?.data?.forEach(data => totalMinutes += +data.estimatedTime);

        var result = (totalMinutes / 60);
        return result;
    }

    function calculateTotalHours() {

        var totalMinutes = 0;

        workOrder?.serviceList?.data?.forEach(data => totalMinutes += +data.estimatedTime);
        workOrder?.materialList?.data?.forEach(data => totalMinutes += +data.estimatedTime);
        workOrder?.otherList?.data?.forEach(data => totalMinutes += +data.estimatedTime);

        let mins = totalMinutes;
        let hours = Math.floor(mins / 60); // Get the number of full hours
        let minutes = mins % 60; // Get the remaining minutes
        if (hours === 0) {
            return `${minutes} mins`;
        } else {
            return `${hours} hr ${minutes} mins`;
        }

    }


    const formatNumberToUs = (number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(number);
    };


    function calculateMaterialCost() {
        var totalCost = 0;
        workOrder.materialList.data.forEach(mat => { totalCost += +returnDiscountedAmount({ cost: mat.cost, discount: mat.discount, discountType: mat.discountType }) });
        return totalCost
    }

    function calculateBundleCost() {
        var totalCost = 0;
        workOrder.serviceBundle.data.forEach(bundle => { totalCost += +returnDiscountedAmount({ cost: bundle.cost, discount: bundle.discount, discountType: bundle.discountType }) });
        return totalCost
    }

    function calculateServiceCost() {
        var totalCost = 0;
        workOrder.serviceList.data.forEach(service => service.costType === 'Fixed' && (totalCost += +returnDiscountedAmount({ cost: service.estimatedCost, discount: service.discount, discountType: service.discountType })));
        return totalCost
    }

    const convertToDate = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        return `${month}-${day}-${year}`;
    }

    const totalDiscount = calculateTotalDiscount(workOrder.materialList, workOrder.serviceList, workOrder.serviceBundle)

    const formatTime = (time) => {
        const [hour, minutes] = time.split(':')
        const hourInt = parseInt(hour);
        const ampm = hourInt >= 12 ? 'PM' : 'AM';
        const hour12 = hourInt % 12 || 12;
        const newTime = `${hour12}:${minutes} ${ampm}`;
        return newTime
    };


    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b z-10 py-3 px-4">
                <div className="flex items-center">
                    <button className={btnRound} onClick={() => setOrderState('create')}>
                        <img src={arrowBlack} alt="back" />
                    </button>
                    <h2 className="ml-2 text-lg font-semibold text-gray-900">Preview Estimate</h2>
                </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
                {/* Service Bundle Section */}
                {workOrder.serviceBundle?.data?.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b">
                            <h3 className="text-sm font-semibold text-gray-900">Service Bundle</h3>
                        </div>
                        <div className="divide-y">
                            {workOrder.serviceBundle.data.map((bundle, index) => (
                                <div key={index} className="px-4 py-3 flex justify-between items-center">
                                    <span className="text-sm text-gray-700 capitalize">{bundle.name}</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        ${formatNumberToUs(returnDiscountedAmount({ cost: bundle.cost, discount: bundle.discount, discountType: bundle.discountType }))}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Services Section */}
                {workOrder.serviceList?.data?.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b">
                            <h3 className="text-sm font-semibold text-gray-900">Services</h3>
                        </div>
                        <div className="divide-y">
                            {workOrder.serviceList.data.map((service, index) => (
                                <div key={index} className="px-4 py-3 flex justify-between items-center">
                                    <span className="text-sm text-gray-700">{service.name}</span>
                                    <span className="text-sm text-gray-500">
                                        {service.costType === 'Fixed' && '$'}
                                        {service.estimatedTime || returnDiscountedAmount({ cost: service.estimatedCost, discount: service.discount, discountType: service.discountType })?.toFixed(2)}
                                        {service.costType === 'Hourly' && ' mins'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Others Section */}
                {workOrder.otherList?.data?.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b">
                            <h3 className="text-sm font-semibold text-gray-900">Other Services</h3>
                        </div>
                        <div className="divide-y">
                            {workOrder.otherList.data.map((other, index) => (
                                <div key={index} className="px-4 py-3 flex justify-between items-center">
                                    <span className="text-sm text-gray-700">{other.name}</span>
                                    <span className="text-sm text-gray-500">{other.estimatedTime} mins</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Parts Section */}
                {workOrder.materialList?.data?.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b">
                            <h3 className="text-sm font-semibold text-gray-900">Parts</h3>
                        </div>
                        <div className="divide-y">
                            {workOrder.materialList.data.map((material, index) => (
                                <div key={index} className="px-4 py-3">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-sm text-gray-700">{material.name}</span>
                                        <span className="text-sm font-medium text-gray-900">${formatNumberToUs(returnDiscountedAmount({ cost: material.cost, discount: material.discount, discountType: material.discountType }))}</span>
                                    </div>
                                    <div className="text-xs text-gray-500">Estimated Time: {material.estimatedTime} mins</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Summary Section */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b">
                        <h3 className="text-sm font-semibold text-gray-900">Summary</h3>
                    </div>
                    <div className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Total Estimated Time</span>
                            <span className="text-sm font-medium text-gray-900">{calculateTotalHours()}</span>
                        </div>

                        {workOrder.materialList?.data?.length > 0 && (
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Total Parts Cost</span>
                                <span className="text-sm font-medium text-gray-900">${formatNumberToUs(calculateMaterialCost())}</span>
                            </div>
                        )}

                        {workOrder.serviceBundle?.data?.length > 0 && (
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Total Service Bundle Cost</span>
                                <span className="text-sm font-medium text-gray-900">${formatNumberToUs(calculateBundleCost())}</span>
                            </div>
                        )}

                        <div className="pt-2 border-t">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-600">Labor Cost (${formatNumberToUs(technicianCost)}/hr)</span>
                                <span className="text-sm font-medium text-gray-900">
                                    ${formatNumberToUs((calculateTotalMinutes() * technicianCost).toFixed(2))}
                                </span>
                            </div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-600">Subtotal</span>
                                <span className="text-sm font-medium text-gray-900">
                                    ${formatNumberToUs(Number((calculateTotalMinutes() * technicianCost).toFixed(2)) + Number(calculateMaterialCost()) + Number(calculateBundleCost()) + Number(calculateServiceCost()))}
                                </span>
                            </div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-600">Discount</span>
                                <span className="text-sm font-medium text-gray-900">
                                    ${totalDiscount !== 0 ? totalDiscount?.toFixed(2) : totalDiscount.toFixed(2)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-600">Sales Tax (${defaultTaxCost}%)</span>
                                <span className="text-sm font-medium text-gray-900">
                                    ${formatNumberToUs((calculateMaterialCost()) * (defaultTaxCost / 100))}
                                </span>
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t">
                                <span className="text-base font-semibold text-gray-900">Final Cost</span>
                                <span className="text-base font-semibold text-customOne">
                                    ${formatNumberToUs(Number((calculateTotalMinutes() * technicianCost).toFixed(2)) + Number(calculateMaterialCost()) + Number(calculateBundleCost()) + Number(calculateServiceCost()) + Number((calculateMaterialCost()) * (defaultTaxCost / 100)))}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Image Previews */}
                {(workOrder.images?.length > 0 || estimateImage.length > 0) && (
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b">
                            <h3 className="text-sm font-semibold text-gray-900">Images</h3>
                        </div>
                        <div className="p-4 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                            {workOrder.images?.map((imageName, index) => (
                                <div key={`server-${index}`} className="relative group">
                                    <img
                                        src={`${url}/fleet_estimate_images/${imageName}`}
                                        alt={`uploaded-${index}`}
                                        className="w-full h-32 object-cover rounded-sm"
                                    />
                                </div>
                            ))}
                            {estimateImage.length > 0 && estimateImage.map((image, index) => (
                                <div key={index}>
                                    <img
                                        src={URL.createObjectURL(image)}
                                        alt={`preview-${index}`}
                                        className="w-full h-32 object-cover rounded"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {/* Completion Details */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b">
                        <h3 className="text-sm font-semibold text-gray-900">Completion Details</h3>
                    </div>
                    <div className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Estimated Completion Date</span>
                            <span className="text-sm font-medium text-gray-900">{convertToDate(workOrder.estimatedDate)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Estimated Completion Time</span>
                            <span className="text-sm font-medium text-gray-900">{formatTime(workOrder.estimatedTime)}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full p-4">
                <button
                    className={btnOne}
                    onClick={() => updateFleetWorkOrder()}
                >
                    Save Estimates
                </button>
            </div>
        </div >
    )
}

export default FleetPreviewEstimate;